// src/lib/inboxSearch.js — one reading of a Gmail-style search, shared by every
// source the inbox searches.
//
// Three sources answer a search:
//   indexed   email_intelligence (analyzed mail), PostgREST filters
//   archive   saved_emails full text (search_saved_emails RPC), every saved message
//   gmail     Gmail's own search (/api/gmail-archive-search), the raw query
//
// The rule: no source may return a result that violates the query the person
// typed. Gmail honours every operator. The two local sources honour the ones
// below; when a query uses anything else (to:, cc:, label:, filename:, in:,
// OR, braces, …) the local sources step aside for that search and Gmail alone
// answers — and the list says so (`localUnsupported`). An operator is never
// silently dropped while the rest of the query still runs locally.
//
// Locally honoured:  words (all must match)   "exact phrase"   -word
//                    from:x  -from:x  subject:x  -subject:x  has:attachment
//                    is:unread  is:read  is:starred  before:DATE  after:DATE

const LOCAL_OPS = new Set(['from', 'subject', 'has', 'is', 'before', 'after'])

/** Tokenize respecting "quoted phrases" and op:"quoted values". */
function tokenize(q) {
  const out = []
  const re = /(-?)([a-z_]+):("([^"]*)"|\(([^)]*)\)|(\S+))|(-?)"([^"]*)"|(\S+)/gi
  let m
  while ((m = re.exec(q)) !== null) {
    if (m[2]) {
      out.push({ kind: 'op', neg: m[1] === '-', op: m[2].toLowerCase(), value: m[4] ?? m[5] ?? m[6] ?? '', raw: m[0], grouped: m[5] !== undefined })
    } else if (m[8] !== undefined) {
      out.push({ kind: 'phrase', neg: m[7] === '-', value: m[8], raw: m[0] })
    } else if (m[9]) {
      out.push({ kind: 'word', raw: m[9] })
    }
  }
  return out
}

export function parseInboxQuery(query) {
  const q = String(query || '').trim()
  const parsed = {
    raw: q,
    terms: [], excluded: [],
    from: [], notFrom: [], subject: [], notSubject: [],
    hasAttachment: false, is: [], before: null, after: null,
    unsupported: [],
  }
  for (const t of tokenize(q)) {
    if (t.kind === 'word') {
      const w = t.raw
      // Boolean grouping ({a b}, a OR b, a | b) has no local equivalent.
      if (/^(OR|AND)$/.test(w) || /[{}|]/.test(w)) { parsed.unsupported.push(w); continue }
      if (w.startsWith('-') && w.length > 1) parsed.excluded.push(w.slice(1))
      else if (w !== '-') parsed.terms.push(w)
      continue
    }
    if (t.kind === 'phrase') {
      if (!t.value.trim()) continue
      ;(t.neg ? parsed.excluded : parsed.terms).push(t.value)
      continue
    }
    const { op, value, neg } = t
    if (!LOCAL_OPS.has(op) || t.grouped) { parsed.unsupported.push(t.raw); continue }
    if (op === 'from') (neg ? parsed.notFrom : parsed.from).push(value)
    else if (op === 'subject') (neg ? parsed.notSubject : parsed.subject).push(value)
    else if (op === 'has') {
      if (value.toLowerCase() === 'attachment' && !neg) parsed.hasAttachment = true
      else parsed.unsupported.push(t.raw)
    } else if (op === 'is') {
      const v = value.toLowerCase()
      if (!neg && ['unread', 'read', 'starred'].includes(v)) parsed.is.push(v)
      else parsed.unsupported.push(t.raw)
    } else if (op === 'before' || op === 'after') {
      const d = parseGmailDate(value)
      if (!d || neg) parsed.unsupported.push(t.raw)
      else parsed[op] = d
    }
  }
  return parsed
}

/** Gmail accepts YYYY/MM/DD, YYYY-MM-DD and epoch seconds. */
export function parseGmailDate(v) {
  const s = String(v || '').trim()
  if (/^\d{9,11}$/.test(s)) return new Date(Number(s) * 1000).toISOString()
  const m = s.match(/^(\d{4})[/-](\d{1,2})[/-](\d{1,2})$/)
  if (!m) return null
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])))
  return Number.isNaN(d.getTime()) ? null : d.toISOString()
}

/** Can the local sources answer this query without breaking it? */
export const localCanHonor = parsed => parsed.unsupported.length === 0

/**
 * Text for the full-text RPC (websearch_to_tsquery): required words, "phrases"
 * kept as phrases, and -excluded words. Operators are NOT passed — the rows
 * that come back are checked against them with rowMatches().
 */
export function fullTextTerms(parsed) {
  const q = w => (/\s/.test(w) ? `"${w.replace(/"/g, '')}"` : w.replace(/"/g, ''))
  return [...parsed.terms.map(q), ...parsed.excluded.map(w => `-${q(w)}`)].join(' ').trim()
}

// PostgREST or() values are quoted so commas, parentheses and dots in what the
// person typed are data, never filter syntax.
const pgQuote = v => `"${String(v).replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`
const likeOf = v => `%${String(v).replace(/[%_]/g, m => `\\${m}`)}%`

/**
 * Apply a parsed query to a PostgREST builder on email_intelligence. Each
 * clause is ANDed; within a clause the fields are ORed. Synchronous — returns
 * the builder (see buildListQuery's note on thenables).
 */
export function applyToIndexedQuery(q, parsed) {
  for (const f of parsed.from) {
    const v = pgQuote(likeOf(f)); q = q.or(`from_email.ilike.${v},from_name.ilike.${v}`)
  }
  // A NULL column must not make a row disappear from a negative filter.
  const notLike = (col, v) => `${col}.is.null,${col}.not.ilike.${pgQuote(likeOf(v))}`
  for (const f of parsed.notFrom) {
    q = q.or(notLike('from_email', f)).or(notLike('from_name', f))
  }
  for (const s of parsed.subject) q = q.ilike('subject', likeOf(s))
  for (const s of parsed.notSubject) q = q.or(notLike('subject', s))
  if (parsed.hasAttachment) q = q.eq('has_attachments', true)
  for (const v of parsed.is) {
    if (v === 'unread') q = q.eq('is_read', false)
    if (v === 'read') q = q.eq('is_read', true)
    if (v === 'starred') q = q.eq('is_starred', true)
  }
  if (parsed.before) q = q.lt('sent_at', parsed.before)
  if (parsed.after) q = q.gt('sent_at', parsed.after)
  for (const t of parsed.terms) {
    const v = pgQuote(likeOf(t))
    q = q.or(`subject.ilike.${v},from_name.ilike.${v},from_email.ilike.${v},summary.ilike.${v}`)
  }
  for (const t of parsed.excluded) {
    q = q.or(notLike('subject', t)).or(notLike('from_name', t)).or(notLike('from_email', t)).or(notLike('summary', t))
  }
  return q
}

const has = (hay, needle) => String(hay || '').toLowerCase().includes(String(needle).toLowerCase())

/**
 * Does a row satisfy the parsed query? Used for the offline mirror and to
 * check full-text results, which match words anywhere in the body and know
 * nothing about operators. `fields.text` is extra text the row matched on.
 */
export function rowMatches(row, parsed) {
  const fromName = row.from_name ?? row.sender_name
  const fromEmail = row.from_email ?? row.sender_email
  const sentAt = row.sent_at || row.date
  if (parsed.from.some(f => !has(fromEmail, f) && !has(fromName, f))) return false
  if (parsed.notFrom.some(f => has(fromEmail, f) || has(fromName, f))) return false
  if (parsed.subject.some(s => !has(row.subject, s))) return false
  if (parsed.notSubject.some(s => has(row.subject, s))) return false
  if (parsed.hasAttachment && row.has_attachments === false) return false
  if (parsed.hasAttachment && row.has_attachments === undefined && !Array.isArray(row.attachments)) return false
  if (parsed.is.includes('unread') && row.is_read !== false) return false
  if (parsed.is.includes('read') && row.is_read === false) return false
  if (parsed.is.includes('starred') && row.is_starred !== true) return false
  if (parsed.before && !(sentAt && sentAt < parsed.before)) return false
  if (parsed.after && !(sentAt && sentAt > parsed.after)) return false
  return true
}

/** Words-and-phrases check for rows whose text we have (offline mirror). */
export function rowMatchesTerms(row, parsed) {
  const hay = [row.subject, row.from_name, row.from_email, row.sender_name, row.sender_email, row.summary, row.snippet].join(' ')
  return parsed.terms.every(t => has(hay, t)) && !parsed.excluded.some(t => has(hay, t))
}
